import { T } from '@tolgee/react';
import { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';

import theme from '../../assets/theme';
import { NewRelic } from '../../hooks/useNewRelic';
import { RecoverableCriticalApplicationError } from '../../types/errors';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

// A recoverable error boundary represents an error caught early enough/low enough in the dom that the whole page doesn't crash
// and a user can work around the error
export class RecoverableErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // this is copied from Client500.tsx
    try {
      // Create a RecoverableCriticalApplicationError with the caught error as inner error
      const criticalError = new RecoverableCriticalApplicationError(
        `Application render error: ${error.message}`,
        'client',
        error,
      );

      // Use the original simple error reporting logic
      if (typeof window == 'undefined') {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const newrelic = require('newrelic');
        newrelic?.noticeError(criticalError);
      } else {
        NewRelic()?.noticeError(criticalError);
      }

      // Log additional context for debugging
      // eslint-disable-next-line no-console
      console.error('Recoverable Critical application error:', criticalError);
      // eslint-disable-next-line no-console
      console.error('Component stack:', errorInfo.componentStack);
    } catch (err) {}
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback>
          <ErrorMessage>
            <T
              keyName="ErrorBoundary.SomethingWentWrong"
              defaultValue="<ErrorIcon>⚠️</ErrorIcon> Something went wrong"
              params={{
                ErrorIcon: <ErrorIcon />,
              }}
            />
          </ErrorMessage>
          <ErrorDetails>
            {this.state.error?.message || (
              <T
                keyName="An unexpected error occurred"
                defaultValue="An unexpected error occurred"
              />
            )}
          </ErrorDetails>
        </ErrorFallback>
      );
    }

    return this.props.children;
  }
}

const ErrorFallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  background-color: ${theme.colors.greyLight};
  border-radius: 8px;
  padding: 20px;
  text-align: center;
`;

// eslint-disable-next-line @typescript-eslint/ban-types
function ErrorIcon({ children }: React.PropsWithChildren<{}>) {
  return <div style={{ fontSize: '24px', marginBottom: '8px' }}>{children}</div>;
}
`
  font-size: 24px;
  margin-bottom: 8px;
`;

const ErrorMessage = styled.h3`
  color: ${theme.colors.black};
  font-size: 14px;
  margin: 0 0 8px 0;
  font-weight: 600;
`;

const ErrorDetails = styled.p`
  color: ${theme.colors.iconGrey};
  font-size: 12px;
  margin: 0;
  word-break: break-word;
`;
